import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { UseInfiniteScrollOptions } from './useInfiniteScroll.types';

/**
 * Returns whether the scroll container is within `scrollOffset` pixels of its bottom.
 */
export const isNearBottom = (target: HTMLElement, scrollOffset: number) =>
  target.scrollHeight - target.scrollTop - target.clientHeight < scrollOffset;

/**
 * Returns whether the scroll container content does not fill its visible area.
 */
export const isUnderflowing = (target: HTMLElement) =>
  target.clientHeight > 0 && target.scrollHeight <= target.clientHeight;

/**
 * Encapsulates near-bottom and underflow infinite-scroll behaviour for a scroll
 * container. Returns a scroll event handler and automatically checks for
 * underflow when `contentDependency` changes.
 *
 * Duplicate load requests in the same tick are suppressed until `isLoading`
 * becomes false again.
 */
export const useInfiniteScroll = (
  containerRef: React.RefObject<HTMLElement | null>,
  {
    scrollOffset = 200,
    isLoading = false,
    onLoadMore,
    hasContent = true,
    contentDependency,
  }: UseInfiniteScrollOptions
): React.UIEventHandler<HTMLElement> => {
  const loadMorePendingRef = useRef(false);
  const onLoadMoreRef = useRef(onLoadMore);

  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!isLoading) {
      loadMorePendingRef.current = false;
    }
  }, [isLoading]);

  const tryLoadMore = useCallback(
    (target: HTMLElement, { checkNearBottom }: { checkNearBottom: boolean }) => {
      if (isLoading || !onLoadMoreRef.current || loadMorePendingRef.current) {
        return;
      }

      const shouldLoad =
        isUnderflowing(target) ||
        (checkNearBottom && isNearBottom(target, scrollOffset));

      if (shouldLoad) {
        loadMorePendingRef.current = true;
        onLoadMoreRef.current();
      }
    },
    [isLoading, scrollOffset]
  );

  const handleScroll = useCallback<React.UIEventHandler<HTMLElement>>(
    (event) => {
      tryLoadMore(event.currentTarget, { checkNearBottom: true });
    },
    [tryLoadMore]
  );

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container || !hasContent) {
      return;
    }

    tryLoadMore(container, { checkNearBottom: false });
  }, [containerRef, contentDependency, hasContent, tryLoadMore]);

  return handleScroll;
};

export default useInfiniteScroll;
